import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faClipboard,
  faPlus,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import config from "../../../config";
import { getlist } from "../../../contexts/apicontexts";
import { useTheme } from "../../../contexts/themContext";
import {
  formatDateTime,
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getPriorityClasses,
  getStatusClasses,
  normalizeTicketData,
} from "../ticketUtils";

const BRAND_LR  = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

export default function AdminCreatedTicketsPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const fetchTickets = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const response = await getlist(`${config.baseurl}Ticket/getadmincreatedlist`);
      if (response?.status === 200) {
        setTickets(Array.isArray(response.data) ? response.data.map(normalizeTicketData) : []);
      } else { setError("Admin-created tickets could not be loaded."); }
    } catch { setError("Admin-created tickets could not be loaded."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const filteredTickets = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const statusFiltered = statusFilter === "All" ? tickets : tickets.filter((t) => t.status === statusFilter);
    if (!query) return statusFiltered;
    return statusFiltered.filter((ticket) =>
      [ticket.ticketNumber, ticket.subject, ticket.status, ticket.category, ticket.priority, ticket.contactEmail, ticket.domainUrl, ticket.customerName, ticket.organizationName, ticket.requesterName, ticket.requesterEmail]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(query))
    );
  }, [searchTerm, statusFilter, tickets]);

  const counts = useMemo(() => ({
    total: tickets.length,
    new: tickets.filter((t) => t.status === "New").length,
    inProgress: tickets.filter((t) => t.status === "In Progress").length,
    onHold: tickets.filter((t) => t.status === "On Hold").length,
    resolved: tickets.filter((t) => t.status === "Resolved").length,
  }), [tickets]);

  return (
    <div className="min-h-screen p-3 md:p-5 bg-gray-50 dark:bg-[#0F111A]">
      <Breadcrumb pageName="Admin Created Tickets" currentPage="Admin Created Tickets" />

      <div className="mt-4 overflow-hidden rounded-3xl border shadow-xl border-orange-100 bg-white dark:bg-[#161B22] dark:border-gray-800" style={{ boxShadow: "0 16px 48px rgba(192,76,42,0.10)" }}>

        {/* ── Hero Header ── */}
        <div className="relative overflow-hidden px-4 py-5 text-white sm:px-6" style={{ background: BRAND_135 }}>
          <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/4 h-24 w-24 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />

          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
                <FontAwesomeIcon icon={faClipboard} />
                Admin Ticket History
              </div>
              <h1 className="mt-2 text-lg font-extrabold tracking-tight text-white sm:text-xl">Tickets opened by admins</h1>
              <p className="mt-1 text-[11px] text-orange-200">
                Support requests created from the admin site on behalf of customers.
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2 text-[11px] text-orange-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5">{identity.fullName || "User"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5">{identityRoleLabel}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5">{identity.email || "No email"}</span>
              </div>
            </div>

            {/* stat cards + buttons */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="grid grid-cols-5 gap-1.5">
                {[
                  { label: "Total",    value: counts.total,      key: "All",         bg: "rgba(255,255,255,0.13)" },
                  { label: "New",      value: counts.new,        key: "New",         bg: "rgba(96,165,250,0.22)" },
                  { label: "Progress", value: counts.inProgress, key: "In Progress", bg: "rgba(251,191,36,0.22)" },
                  { label: "On Hold",  value: counts.onHold,     key: "On Hold",     bg: "rgba(251,146,60,0.22)" },
                  { label: "Resolved", value: counts.resolved,   key: "Resolved",    bg: "rgba(52,211,153,0.22)" },
                ].map(({ label, value, key, bg }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatusFilter(key)}
                    className="rounded-xl border px-2 py-1.5 text-center transition"
                    style={{
                      background: bg,
                      borderColor: statusFilter === key ? "#fff" : "rgba(255,255,255,0.15)",
                      boxShadow: statusFilter === key ? "0 0 0 2px rgba(255,255,255,0.5)" : "none",
                    }}
                  >
                    <div className="text-[9px] font-bold uppercase tracking-widest text-orange-100">{label}</div>
                    <div className="text-base font-extrabold leading-tight text-white">{value}</div>
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/tickets/admin/new-request")}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/15 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/25"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Support Request
                </button>
                <button
                  type="button"
                  onClick={fetchTickets}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Search ── */}
        <div className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:px-6 border-orange-100 bg-gradient-to-r from-orange-50/60 to-red-50/40 dark:bg-[#161B22] dark:border-gray-800">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border px-3 py-2 text-sm outline-none transition min-w-[150px] border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
            onBlur={(e)  => { e.target.style.borderColor = ""; }}
          >
            <option value="All">All Status</option>
            <option value="New">New</option>
            <option value="In Progress">In Progress</option>
            <option value="On Hold">On Hold</option>
            <option value="Resolved">Resolved</option>
          </select>

          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: "#C04C2A" }} />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by subject, requester, customer, or ticket number…"
              className="w-full rounded-xl border py-2 pl-9 pr-3 text-sm outline-none transition border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
              onBlur={(e)  => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
            />
          </div>
        </div>

        {/* ── Content ── */}
        <div className="p-4 sm:p-5">
          {error ? (
            <div className="mb-3 rounded-xl border px-4 py-2.5 text-sm border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/30 px-6 py-14 text-center text-sm" style={{ color: "#C04C2A" }}>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading admin-created tickets…
            </div>
          ) : filteredTickets.length ? (
            <div className="space-y-2.5">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/tickets/${ticket.id}`, { state: { backTo: "/tickets/admin-created" } })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/tickets/${ticket.id}`, { state: { backTo: "/tickets/admin-created" } });
                    }
                  }}
                  className="group relative w-full cursor-pointer overflow-hidden rounded-2xl border text-left transition hover:shadow-md bg-white dark:bg-[#161B22] hover:bg-gray-50 dark:hover:bg-gray-800"
                  style={{
                    borderColor: ticket.hasUnread ? "#fcd34d" : isDarkMode ? "rgba(100,116,139,0.4)" : "rgba(192,76,42,0.12)",
                  }}
                >
                  {/* left accent bar */}
                  <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: ticket.hasUnread ? "#fbbf24" : BRAND_LR }} />

                  <div className="flex flex-col gap-3 p-3 pl-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-lg border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border-slate-200 bg-slate-100 text-slate-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                          {ticket.ticketNumber}
                        </span>
                        <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${getStatusClasses(ticket.status)}`}>{ticket.status}</span>
                        <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${getPriorityClasses(ticket.priority)}`}>{ticket.priority}</span>
                        <span className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">Admin created</span>
                        {ticket.customerName ? (
                          <span className="rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[10px] font-bold text-cyan-700">{ticket.customerName}</span>
                        ) : null}
                      </div>
                      <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-white">{ticket.subject}</h3>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400">
                        <span>{ticket.category}</span>
                        <span>{ticket.contactEmail}</span>
                        <span>{ticket.domainUrl || "No domain"}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
                        <span>Requester: {ticket.requesterName || ticket.userName || "—"}</span>
                        {ticket.organizationName ? <span>Org: {ticket.organizationName}</span> : null}
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      <div className="text-[11px] text-slate-400">Opened {formatDateTime(ticket.createdAt)}</div>
                      <div className="text-[11px] text-slate-400">Updated {formatDateTime(ticket.lastReplyAt || ticket.createdAt)}</div>
                      <div className="mt-2 flex flex-wrap justify-end gap-1.5">
                        <Link
                          to={`/tickets/${ticket.id}?${new URLSearchParams({ backTo: "/tickets/admin-created" })}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-white transition hover:opacity-90"
                          style={{ background: BRAND_LR }}
                        >
                          Open <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-2xl border border-dashed px-6 py-14 text-center"
              style={{ borderColor: "rgba(192,76,42,0.22)", background: isDarkMode ? "rgba(192,76,42,0.05)" : "rgba(192,76,42,0.02)" }}
            >
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: BRAND_LR }}>
                <FontAwesomeIcon icon={faTicket} />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">No admin-created tickets found</h3>
              <p className="mt-1.5 text-sm text-slate-500 dark:text-gray-400">Open a support request from the admin site to see it here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
